import {
  Button,
  Box,
  VStack,
  Icon,
  useToast,
  Flex,
  useDisclosure,
  useBreakpointValue,
  PlacementWithLogical,
  Grid,
  GridItem,
  Heading,
  useColorModeValue,
} from "@chakra-ui/react";
import { doc, getDoc, setDoc, getFirestore } from "firebase/firestore/lite";
import { IoArrowForward } from "react-icons/io5";
import useAuthContext from "~/context/auth.context";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useEffect, useRef, useState } from "react";
import useGoogleAnalytics from "~/hooks/useGoogleAnalytics";
import { showError, pickWalletForProject } from "~/core/helpers";
import { ProjectDocument } from "~/core/types";
import api from "~/core/api";
import RegistrationCountdown from "~/components/RegistrationCountdown";
import { useRouter } from "next/router";
import { app } from "~/core/initFirebase";
import useEvmLogin from "~/hooks/useEvmLogin";
import useFlowLogin from "~/hooks/useFlowLogin";
import useSolanaLogin from "~/hooks/useSolanaLogin";
import ProjectMetadata from "./ProjectMetadata";
import useLogEvent from "~/hooks/useLogEvent";
import DetailsSection from "./SingleProject/DetailsSection";
import HeadingSection from "./SingleProject/HeadingSection";
import VerifyEmailRequirement from "./SingleProject/VerifyEmailRequirement";
import ConnectTwitterRequirement from "./SingleProject/ConnectTwitterRequirement";
import ConnectDiscordRequirement from "./SingleProject/ConnectDiscordRequirement";
import CustomFieldRequirement from "./SingleProject/CustomFieldRequirement";
import ConnectWalletRequirement from "./SingleProject/ConnectWalletRequirement";
import FeaturedProject from "./FeaturedProject";
import RegisterYourEmailModal from "./RegisterYourEmailModal";
import { captureException, captureMessage } from "@sentry/nextjs";
import { LoginModal } from "./LoginModal";
import { getEntryId, setEntryId } from "~/core/safeLocalStorage";
import { CAPTCHA_KEY } from "~/core/constants";
import { claimTo } from "~/core/minter";
import useRegistrationTimeContext, {
  RegistrationStatus,
  RegistrationTimeProvider,
} from "~/context/RegistrationTime.context";
import { PixelScript, sendCustomPixelEvent } from "~/core/metaPixel";

const db = getFirestore(app);
const functions = getFunctions(app);
// Uncomment next line to use local emulator for callable functions
// connectFunctionsEmulator(functions, "localhost", 5001);

const buttonTextByStatus = {
  [RegistrationStatus.willOpen]: "Registration opening soon",
  [RegistrationStatus.alwaysOpen]: "Click to Register",
  [RegistrationStatus.willClose]: "Click to Register",
  [RegistrationStatus.closed]: "Registration is closed",
};

function SingleProject({
  project,
  featuredProjects,
}: {
  project: ProjectDocument;
  featuredProjects: ProjectDocument[];
}) {
  const logEvent = useLogEvent();
  //#region HOOKS
  const router = useRouter();

  const [registerLoading, setRegisterLoading] = useState(false);
  const [isUnregisterLoading, setIsUnregisterLoading] = useState(false);
  const [isMintLoading, setIsMintLoading] = useState(false);
  const [customFieldValue, setCustomFieldValue] = useState("");
  const [isUserRegistered, setIsUserRegistered] = useState(false);
  const [userMintAddress, setUserMintAddress] = useState();
  const [isRegisterYourEmailModalOpen, setIsRegisterYourEmailModalOpen] =
    useState(false);
  const {
    isOpen: isLoginModalOpen,
    onOpen: onLoginModalOpen,
    onClose: onLoginModalClose,
  } = useDisclosure();
  const { evmLogin, evmLoading, evmElement } = useEvmLogin();
  const { flowLogin, flowLoading } = useFlowLogin();
  const { solanaLogin, solanaLoading, solanaElement } = useSolanaLogin();

  const {
    user,
    userData,
    twitterData,
    discordData,
    clearDiscordData,
    clearTwitterData,
  } = useAuthContext();
  const { status } = useRegistrationTimeContext();

  const isToastFired = useRef(false);

  const toast = useToast({
    position: "top",
    isClosable: true,
  });
  const tooltipPosition = useBreakpointValue<PlacementWithLogical>({
    base: "top",
    xl: "left",
  });

  //#endregion

  //#region EFFECTS
  useEffect(() => {
    const { tStatus } = router.query;
    if (tStatus && tStatus !== "ok" && !isToastFired.current) {
      isToastFired.current = true;
      toast({ description: tStatus });
    }
  }, [router.query, toast]);

  useEffect(() => {
    if (user?.uid) {
      getDoc(doc(db, "projects", project.id, "preusers", user?.uid))
        .then((doc) => {
          if (doc.exists() && doc.data().customfield) {
            setCustomFieldValue(doc.data().customfield);
          }
        })
        .catch((error) => {
          captureMessage("Error preusers data in SingleProject.tsx");
          captureException(error);
          showError(error, true);
        });
    }
  }, [userData, project, user?.uid]);

  useEffect(() => {
    if (!user) {
      setIsUserRegistered(false);
      return;
    }

    getDoc(doc(db, `projects/${project.id}/users`, user.uid))
      .then((doc) => {
        if (doc.exists()) {
          setIsUserRegistered(true);
        }
        if (doc.get("mintable") && doc.get("walletAddress")) {
          console.log("doc.get('walletAddress')", doc.get("walletAddress"));
          setUserMintAddress(doc.get("walletAddress"));
        }
      })
      .catch((error) => {
        captureMessage("Error fetching users data in SingleProject.tsx");
        captureException(error);
        showError(error, true);
      });
  }, [project, user]);
  //#endregion

  useGoogleAnalytics(project?.gaId);

  const handleChangeCustomField = async (value: string) => {
    if (project?.id && user?.uid) {
      const userprojref = doc(
        db,
        "projects",
        project.id,
        "preusers",
        user?.uid
      );
      await setDoc(userprojref, { customfield: value }, { merge: true });
    }
  };
  const handlePixelEvent = () => {
    if (!project) return;

    try {
      if ((window as any)?.fbq) {
        (window as any)?.fbq("track", "SubmitApplication", {
          hype: true,
          project: project?.name,
          wallet: userWallet,
          email: project?.email?.isEmailRequired ? userData?.email : undefined,
          phone: project?.phone?.isPhoneRequired ? userData?.phone : undefined,
          userTwitter: twitterData?.data,
          userDiscord: discordData?.userInfo,
        });
      }
    } catch (err) {
      console.warn("Pixel event not sent", err);
    }
  };
  const validateCustomField = (): boolean => {
    const isValid = !(project?.customfield?.enabled && !customFieldValue);
    if (!isValid) {
      // showError(toast, {
      //   message: `Required field is not filled. ${project?.customfield?.labels[0]}`,
      // });

      setIsCustomFieldTooltipOpen(true);
      setTimeout(() => {
        setIsCustomFieldTooltipOpen(false);
      }, 10000);
    }

    return isValid;
  };
  const triggerRequirementTooltip = (error: {
    code: string;
    message: string;
  }) => {
    setRegisterationErrorMessage(error.message);
    switch (error.code) {
      case "validateForProject-06":
        setIsVerifyEmailTooltipOpen(true);
        break;

      case "validateForProject-11":
        setIsCustomFieldTooltipOpen(true);
        break;

      case "validateForProject-12":
        setIsWalletBalanceTooltipOpen(true);
        break;

      case "validateForProject-17":
        setIsWalletBalanceTooltipOpen(true);
        break;

      case "validateForProject-13":
      case "validateForProject-19":
        setIsNftTooltipOpen(true);
        break;
      // case "validateForProject-18":
      //   setIsVerifyPhoneTooltipOpen(true);
      //   break;
      case "twitter01":
      case "twitter02":
      case "twitter03":
      case "twitter06":
      case "twitter10":
      case "twitter20":
        clearTwitterData();
        setIsConnectTwitterTooltipOpen(true);
        break;

      case "twitter04":
        setIsFollowOnTwitterTooltipOpen(error.message);
        break;

      case "twitter05":
        setIsRetweetTooltipOpen(true);
        break;

      case "twitter07":
        setIsMinFollowersTooltipOpen(true);
        break;

      case "twitter08":
      case "twitter09":
        setIsTwitterAccountCreatedTooltipOpen(true);
        break;

      case "twitter15":
        setIsLikeTooltipOpen(true);
        break;

      case "discord01":
      case "discord05":
      case "discord08":
        clearDiscordData();
        setIsConnectDiscordTooltipOpen(true);
        break;

      case "discord02":
      case "discord03":
      case "discord04":
      case "discord06":
      case "discord07":
        setIsDiscordReqsTooltipOpen(true);
        break;

      case "discord031":
      case "discord041":
      case "discord061":
      case "discord071":
        setIsDiscordReqsTooltipOpen2(true);
        break;

      default:
        showError(error);
        break;
    }

    setTimeout(clearErrorTooltips, 15000);
  };

  const clearErrorTooltips = () => {
    setIsVerifyEmailTooltipOpen(false);
    // setIsVerifyPhoneTooltipOpen(false);
    setIsConnectDiscordTooltipOpen(false);
    setIsConnectTwitterTooltipOpen(false);
    setIsDiscordReqsTooltipOpen(false);
    setIsRetweetTooltipOpen(false);
    setIsLikeTooltipOpen(false);
    setIsMinFollowersTooltipOpen(false);
    setIsCustomFieldTooltipOpen(false);
    setIsFollowOnTwitterTooltipOpen("");
    setIsTwitterAccountCreatedTooltipOpen(false);
    setIsNftTooltipOpen(false);
    setIsWalletBalanceTooltipOpen(false);
  };
  //#endregion

  const tryToRegister = async () => {
    if (!project?.id || isUserRegistered) return;
    clearErrorTooltips();
    setRegisterLoading(true);

    logEvent("registration_started", {
      project: project.slug ? project.slug : project.id,
      chain: project.chain,
    });

    handlePixelEvent();

    if (!validateCustomField()) {
      logEvent("registration_failed", {
        project: project.slug ? project.slug : project.id,
        chain: project.chain,
        failure_reason: "validate custom field failed",
      });

      return setRegisterLoading(false);
    }

    if (customFieldValue) {
      await handleChangeCustomField(customFieldValue);
    }

    if (!(window as any).grecaptcha) {
      const captchaError = new Error(
        "Recaptcha not loaded, please try again. If it occurs again, please contact us."
      );
      captchaError.name = "captchaLoadError";
      captureException(captchaError);
      showError(captchaError);
      logEvent("registration_failed", {
        project: project.slug ? project.slug : project.id,
        chain: project.chain,
        failure_reason: "recaptcha not loaded",
      });

      return setRegisterLoading(false);
    }

    const token = await (window as any)?.grecaptcha?.enterprise?.execute(
      CAPTCHA_KEY,
      { action: "register" }
    );

    const validateForProject = httpsCallable(functions, "validateForProject");
    const result = await validateForProject({
      projectId: project.id,
      captchaToken: token,
      entryId: getEntryId(project.id),
    });

    const data: any = result.data;
    if (data.error) {
      triggerRequirementTooltip(data.error);
      // showError(toast, data.error);
      console.log("validateForProject error:", data.error);
      logEvent("registration_failed", {
        project: project.slug ? project.slug : project.id,
        chain: project.chain,
        failure_reason: data.error?.code || data.error,
      });
      return setRegisterLoading(false);
    }

    setEntryId(project.id, data.entryId);
    setIsUserRegistered(true);

    if (!userData?.email) {
      setIsRegisterYourEmailModalOpen(true);
      logEvent("register_email_modal_open");
    }

    logEvent("registration_success", {
      project: project.slug ? project.slug : project.id,
      chain: project.chain,
    });

    sendCustomPixelEvent("RegistrationCompleted", { uid: user?.uid });

    return setRegisterLoading(false);
  };
  const handleMint = async () => {
    if (!project?.id || !user?.uid || !isUserRegistered) {
      return;
    }
    setIsMintLoading(true);
    // get user under project
    try {
      if (!userMintAddress || !project.nft?.contractAddress) {
        return;
      }
      const result = await claimTo(
        userMintAddress,
        project.nft?.contractAddress,
        project.nft?.chain
      );
      console.log("claimTo result:", result);
      if (result.result.length > 0 && result.result[0].receipt.blockHash) {
        toast({
          title: "Success!",
          description: "NFT is minted successfully",
          status: "success",
        });
      } else {
        toast({
          title: "Error",
          description: "NFT minting failed",
          status: "error",
        });
      }
    } finally {
      setIsMintLoading(false);
    }
  };

  const handleUnregister = async () => {
    if (!project?.id || !user?.uid || !isUserRegistered) return;
    clearErrorTooltips();
    setIsUnregisterLoading(true);

    try {
      await api.unregister(project.id, user.uid);
      toast({
        title: "Success",
        description: "You have successfully unregistered from this campaign.",
        status: "success",
      });
      setIsUserRegistered(false);
    } catch (err) {
      console.log(err);
      showError(err);
    } finally {
      setIsUnregisterLoading(false);
    }
  };

  const [registerationErrorMessage, setRegisterationErrorMessage] =
    useState("");
  const isRegistrationClosed =
    status === RegistrationStatus.closed ||
    status === RegistrationStatus.willOpen;

  const [isWalletBalanceTooltipOpen, setIsWalletBalanceTooltipOpen] =
    useState(false);

  const [isNftTooltipOpen, setIsNftTooltipOpen] = useState(false);

  const [
    isTwitterAccountCreatedTooltipOpen,
    setIsTwitterAccountCreatedTooltipOpen,
  ] = useState(false);

  const [isFollowOnTwitterTooltipOpen, setIsFollowOnTwitterTooltipOpen] =
    useState("");

  const [isMinFollowersTooltipOpen, setIsMinFollowersTooltipOpen] =
    useState(false);

  const [isRetweetTooltipOpen, setIsRetweetTooltipOpen] = useState(false);
  const [isLikeTooltipOpen, setIsLikeTooltipOpen] = useState(false);

  const [isDiscordReqsTooltipOpen, setIsDiscordReqsTooltipOpen] =
    useState(false);
  const [isDiscordReqsTooltipOpen2, setIsDiscordReqsTooltipOpen2] =
    useState(false);

  const userWallet = pickWalletForProject(project?.chain, userData?.wallets);
  const userNftWallet = project?.wallet?.isNftRequired
    ? pickWalletForProject(
        project?.wallet?.nftCollection?.chain || project?.chain,
        userData?.wallets
      )
    : true;
  const nftChain = project?.wallet?.nftCollection?.chain || project?.chain;

  const [isConnectTwitterTooltipOpen, setIsConnectTwitterTooltipOpen] =
    useState(false);

  const [isConnectDiscordTooltipOpen, setIsConnectDiscordTooltipOpen] =
    useState(false);

  const [isVerifyEmailTooltipOpen, setIsVerifyEmailTooltipOpen] =
    useState(false);

  const [isCustomFieldTooltipOpen, setIsCustomFieldTooltipOpen] =
    useState(false);

  const descTextColor = useColorModeValue("gray.800", "gray.300");

  return (
    <>
      <ProjectMetadata project={project} />
      <PixelScript pixelId={project.pixelId} />
      <Flex direction={"column"}>
        <HeadingSection project={project} />

        <Flex
          px={{ xl: "0", base: "6" }}
          mx="auto"
          w={"100%"}
          maxW={{ base: "100vw", xl: 1200 }}
          direction={{ xl: "row", base: "column" }}
        >
          <Box flex={1} pr={{ xl: "3rem", base: 0 }} mb={10}>
            <DetailsSection
              isUserRegistered={isUserRegistered}
              project={project}
            />
          </Box>

          {/* REGISTER */}
          <Box
            minW={{ xl: "30rem", lg: "auto" }}
            maxW={{ xl: "30rem", lg: "auto" }}
          >
            <Box
              py={4}
              boxShadow={{
                base: "none",
                xl: "0 12px 15px rgb(0 0 0 / 10%), 0 17px 50px rgb(0 0 0 / 10%)",
              }}
              borderRadius="3xl"
              borderWidth={4}
              mb={8}
              borderColor={"boxBorder"}
              mt={{ base: 0, xl: "-14.5rem" }}
              position={{ base: "initial", xl: "relative" }}
              backgroundColor={"baseBg"}
            >
              <VStack>
                <RegistrationCountdown projectData={project} />

                <Box w="100%">
                  {/* WALLET */}
                  <ConnectWalletRequirement
                    project={project}
                    userWallet={userWallet}
                    solanaLogin={solanaLogin}
                    solanaLoading={solanaLoading}
                    flowLogin={flowLogin}
                    flowLoading={flowLoading}
                    evmLogin={evmLogin}
                    evmLoading={evmLoading}
                    user={user}
                    tooltipPosition={tooltipPosition}
                    registerationErrorMessage={registerationErrorMessage}
                    isWalletBalanceTooltipOpen={isWalletBalanceTooltipOpen}
                    isNftTooltipOpen={isNftTooltipOpen}
                  />
                </Box>

                <Box w="100%" style={{ marginTop: "0" }}>
                  {/* TWITTER */}
                  <ConnectTwitterRequirement
                    project={project}
                    isTooltipOpen={isConnectTwitterTooltipOpen}
                    tooltipLabel={registerationErrorMessage}
                    tooltipPosition={tooltipPosition}
                    isTwitterAccountCreatedTooltipOpen={
                      isTwitterAccountCreatedTooltipOpen
                    }
                    isFollowOnTwitterTooltipOpen={isFollowOnTwitterTooltipOpen}
                    isRetweetTooltipOpen={isRetweetTooltipOpen}
                    isLikeTooltipOpen={isLikeTooltipOpen}
                    isMinFollowersTooltipOpen={isMinFollowersTooltipOpen}
                  />
                </Box>

                <Box w="100%" style={{ marginTop: "0" }}>
                  {/* DISCORD */}
                  <ConnectDiscordRequirement
                    project={project}
                    field="discord"
                    isTooltipOpen={isConnectDiscordTooltipOpen}
                    tooltipPosition={tooltipPosition}
                    tooltipLabel={registerationErrorMessage}
                    isDiscordReqsTooltipOpen={isDiscordReqsTooltipOpen}
                  />
                </Box>
                <Box w="100%" style={{ marginTop: "0" }}>
                  {/* DISCORD */}
                  <ConnectDiscordRequirement
                    project={project}
                    field="discord2"
                    isTooltipOpen={isConnectDiscordTooltipOpen}
                    tooltipPosition={tooltipPosition}
                    tooltipLabel={registerationErrorMessage}
                    isDiscordReqsTooltipOpen={isDiscordReqsTooltipOpen2}
                  />
                </Box>

                <Box w="100%" style={{ marginTop: "0" }}>
                  {/* EMAIL */}
                  <VerifyEmailRequirement
                    project={project}
                    isTooltipOpen={isVerifyEmailTooltipOpen}
                    tooltipLabel={registerationErrorMessage}
                    tooltipPosition={tooltipPosition}
                  />
                </Box>

                <Box w="100%" style={{ marginTop: "0" }}>
                  {/* Custom Field*/}
                  <CustomFieldRequirement
                    project={project}
                    isTooltipOpen={isCustomFieldTooltipOpen}
                    tooltipPosition={tooltipPosition}
                    customFieldValue={customFieldValue}
                    setCustomFieldValue={setCustomFieldValue}
                  />
                </Box>

                <Box w={"100%"} px={8} py={4}>
                  <Button
                    width={"100%"}
                    height={"4rem"}
                    rightIcon={
                      !isUserRegistered && !isRegistrationClosed ? (
                        <Icon as={IoArrowForward} />
                      ) : undefined
                    }
                    colorScheme={isRegistrationClosed ? "red" : undefined}
                    bg={isRegistrationClosed ? undefined : "green.400"}
                    color={isRegistrationClosed ? undefined : "gray.50"}
                    _hover={
                      isRegistrationClosed
                        ? undefined
                        : {
                            bg: "green.500",
                          }
                    }
                    fontSize={"20px"}
                    onClick={() => {
                      if (project?.wallet?.required === false) {
                        return user ? tryToRegister() : onLoginModalOpen();
                      }

                      if (user && userWallet && userNftWallet)
                        return tryToRegister();

                      const mode = user ? "add" : "login";
                      const chain =
                        user && userWallet ? nftChain : project.chain;
                      if (chain === "solana") {
                        solanaLogin(mode);
                      } else if (chain === "flow") {
                        flowLogin(mode);
                      } else {
                        evmLogin(mode, chain);
                      }
                    }}
                    loadingText="Validating..."
                    isLoading={
                      registerLoading ||
                      solanaLoading ||
                      flowLoading ||
                      evmLoading
                    }
                    disabled={isRegistrationClosed || registerLoading}
                  >
                    {isUserRegistered
                      ? "Registered! 🎉"
                      : buttonTextByStatus[status]}
                  </Button>
                  {userMintAddress && project.nft?.contractAddress && (
                    /* <ClaimNft
                      contractAddress={project.nft?.contractAddress}
                      claimingAddress={userMintAddress}
                    ></ClaimNft>*/
                    <Button
                      float={"left"}
                      mt={6}
                      colorScheme={"red"}
                      variant="solid"
                      size="md"
                      isLoading={isMintLoading}
                      onClick={handleMint}
                    >
                      Mint
                    </Button>
                  )}
                  {isUserRegistered && !isRegistrationClosed ? (
                    <Button
                      float={"right"}
                      mt={6}
                      colorScheme={"red"}
                      variant="ghost"
                      size="md"
                      isLoading={isUnregisterLoading}
                      onClick={handleUnregister}
                    >
                      Unregister
                    </Button>
                  ) : null}
                </Box>
              </VStack>
            </Box>
          </Box>
        </Flex>

        {featuredProjects.length > 0 && (
          <Box
            px={{ xl: "0", base: "6" }}
            mx="auto"
            w={"100%"}
            maxW={{ base: "100vw", xl: 1200 }}
          >
            <Grid mb={6} ml={2}>
              <GridItem colSpan={1}>
                <VStack alignItems="flex-start" spacing="20px">
                  <Heading
                    fontWeight={500}
                    fontSize={"2xl"}
                    mt={20}
                    color={descTextColor}
                    fontFamily="'Bungee Inline', cursive"
                  >
                    Featured Campaigns
                  </Heading>
                </VStack>
              </GridItem>
            </Grid>

            <Grid
              templateColumns={{
                base: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              }}
              gap={8}
            >
              {featuredProjects?.map((fp) => (
                <GridItem key={fp.id}>
                  <FeaturedProject
                    project={fp}
                    actionLabel="View Campaign"
                  ></FeaturedProject>
                </GridItem>
              ))}
            </Grid>
          </Box>
        )}
      </Flex>

      <RegisterYourEmailModal
        isOpen={isRegisterYourEmailModalOpen}
        onClose={() => {
          setIsRegisterYourEmailModalOpen(false);
        }}
      />
      <LoginModal isOpen={isLoginModalOpen} onClose={onLoginModalClose} />
      {evmElement}
      {solanaElement}
    </>
  );
}

export default function WrappedSingleProject({
  project,
  featuredProjects,
}: {
  project: ProjectDocument;
  featuredProjects: ProjectDocument[];
}) {
  return (
    <RegistrationTimeProvider projectData={project}>
      <SingleProject project={project} featuredProjects={featuredProjects} />
    </RegistrationTimeProvider>
  );
}
